// SMPP 抓包调试的接口（docs/04 第 10 节，决策 D93）：任务的新建、停止与列表，抓到的 PDU 按游标分页读，pcap 直接下载。
import { toBeijing, type Tone } from "@/ui";
import { api } from "./api";
import type { Account, Channel } from "./master";
import { nameOf } from "./queries";

export type CaptureState = "running" | "stopped" | "expired";

/** 抓包任务：目标是客户账号或供应商通道，二选一。packets 是已抓到的条数，数据在任务到期 24 小时后清理，之后为 0；Redis 读不了时为 null。 */
export interface Capture {
  id: number; account_id: number | null; channel_id: number | null; max_packets: number; note: string | null;
  created_by: string; started_at: string; expires_at: string; stopped_at: string | null;
  state: CaptureState; packets: number | null;
}

export interface CaptureInput { account_id: number | null; channel_id: number | null; minutes: number; max_packets: number; note: string | null }

/** 解码后的一个 PDU。out 为真是本端（网关）发出；fields 按 SMPP 3.4 的顺序，TLV 在最后；解析失败时只有头部与 error。 */
export interface CapturedPDU {
  id: string; time: string; out: boolean; instance: string; system_id: string; local: string; remote: string;
  command: string; status: number; status_name: string; seq: number; length: number;
  fields: { name: string; value: string }[]; error?: string; hex: string;
}

export interface PacketPage { items: CapturedPDU[]; next: string }

export const captures = {
  list: () => api<Capture[]>("/captures"),
  get: (id: number) => api<Capture>(`/captures/${id}`),
  create: (body: CaptureInput) => api<Capture>("/captures", { method: "POST", body: JSON.stringify(body) }),
  stop: (id: number) => api<Capture>(`/captures/${id}/stop`, { method: "POST" }),
  packets: (id: number, after: string) => api<PacketPage>(`/captures/${id}/packets?limit=1000${after ? `&after=${encodeURIComponent(after)}` : ""}`),
  pcapURL: (id: number) => `/api/captures/${id}/pcap`,
};

export const CAPTURE_STATE: Record<CaptureState, { label: string; tone: Tone }> = {
  running: { label: "抓包中", tone: "success" },
  stopped: { label: "已停止", tone: "neutral" },
  expired: { label: "已到期", tone: "neutral" },
};

/** 一个 PDU 的摘要：请求看地址与内容，响应看 message_id，其余只看命令本身。 */
export function pduSummary(p: CapturedPDU): string {
  const f = Object.fromEntries(p.fields.map((x) => [x.name, x.value]));
  if (p.error) return p.error;
  if (f.destination_addr !== undefined) {
    const addr = (v: string) => v.replace(/^ton=\d+ npi=\d+ /, "");
    return `${addr(f.source_addr)} → ${addr(f.destination_addr)}  ${f.short_message ?? f.short_message_hex ?? ""}`.trim();
  }
  if (f.message_id !== undefined) return `message_id=${f.message_id}`;
  if (f.system_id !== undefined) return `system_id=${f.system_id}`;
  return "";
}

/** 十六进制原文排成每行 16 字节、带偏移的样子，方便对照字段。 */
export function hexDump(hex: string): string {
  const lines: string[] = [];
  for (let i = 0; i < hex.length; i += 32) {
    const bytes = hex.slice(i, i + 32).match(/../g) ?? [];
    lines.push(`${(i / 2).toString(16).padStart(4, "0")}  ${bytes.join(" ")}`);
  }
  return lines.join("\n");
}

/** 时刻精确到毫秒（北京时间），同一秒内的多个 PDU 才分得清先后。 */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : toBeijing(d).toISOString().slice(11, 23);
}

/** 任务对象的显示名：客户账号带 system_id，供应商通道带通道名；列表没加载时显示 ID。 */
export function captureTarget(accountRows: Account[] | undefined, channelRows: Channel[] | undefined) {
  return (c: Capture) => {
    if (c.channel_id != null) return `通道 ${nameOf(channelRows, c.channel_id)}`;
    const a = accountRows?.find((x) => x.id === c.account_id);
    return a ? `账号 ${a.name}（${a.system_id}）` : `账号 #${c.account_id}`;
  };
}
